import type { SyncVendorProp } from "@/src/shared/contracts/app";
import {
  hasProviderAccountConnectionEvidence,
  isOwnerDisconnectedProviderAccount,
  isUsableProviderAccountRow,
  providerAccountRowConnectionState,
} from "./providerAccountConnection";
import { getZohoOAuthPublicConfig } from "./vendors/unified/ownerSetupEmailOAuth";
import {
  normalizePipedriveApiOrigin,
  normalizeZohoApiOrigin,
} from "@/src/features/integrations/vendors/crm/origins";
import { normalizeDynamics365EnvironmentUrl } from "@/src/features/integrations/vendors/crm/dynamics365SalesOAuth";
import { normalizeSalesforceInstanceUrl } from "@/src/features/integrations/vendors/salesforce/contactsApi";
import type { MFilesOAuthPluginConfiguration } from "./vendors/mfiles";
import { logSafeError } from "@smartmanager/shell-ui/shared/logging/safeError";

export type ProviderAccountIdentity = {
  accountId?: string;
  /** Authorized provider tenant/resource IDs; never used as the user-bound dedup identity. */
  providerTenantIds?: string[];
  /** Provider-reported grants, when the identity endpoint exposes them. */
  grantedScopes?: string[];
  email?: string;
  displayName?: string;
};

/**
 * The Microsoft address the tenant vouches for. The UPN's domain is a verified
 * tenant domain; `mail` is admin-writable (nOAuth), so it is trusted only when
 * it sits on the UPN's domain.
 */
export function microsoftVerifiedEmail(userPrincipalName: unknown, mail: unknown): string | undefined {
  const upn = typeof userPrincipalName === "string" ? userPrincipalName.trim() : "";
  const upnDomain = upn.includes("@") ? upn.slice(upn.lastIndexOf("@") + 1).toLowerCase() : "";
  if (!upnDomain) return undefined;
  const mailbox = typeof mail === "string" ? mail.trim() : "";
  const mailDomain = mailbox.includes("@") ? mailbox.slice(mailbox.lastIndexOf("@") + 1).toLowerCase() : "";
  return mailDomain === upnDomain ? mailbox : upn;
}

function boundedIdentityString(value: unknown, maxLength: number): string | undefined {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized && normalized.length <= maxLength ? normalized : undefined;
}

const CRM_NATIVE_ACCOUNT_PROVIDERS = new Set([
  "hubspot",
  "salesforce",
  "pipedrive",
  "zoho_crm",
  "dynamics_365_sales",
  "close",
  "copper",
  "freshsales",
  "attio",
]);

function crmNativeAccountId(value: unknown): string | undefined {
  const normalized = typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? String(value)
    : boundedIdentityString(value, 512);
  return normalized && /^[A-Za-z0-9._-]+$/.test(normalized)
    ? normalized
    : undefined;
}

const ATTIO_SCOPE_PATTERN = /^[a-z][a-z0-9_]*:(?:read|read-write)$/;

function parseAttioGrantedScopes(value: unknown): string[] | null {
  if (typeof value !== "string" || !value.trim() || value.length > 4_096) return null;
  const scopes = Array.from(new Set(value.trim().split(/\s+/)));
  return scopes.length <= 64 && scopes.every((scope) => ATTIO_SCOPE_PATTERN.test(scope))
    ? scopes.sort()
    : null;
}

export function attioScopesMeetReadRequirements(
  grantedScopes: unknown,
  requiredScopes: readonly string[],
): grantedScopes is string[] {
  if (
    !Array.isArray(grantedScopes)
    || grantedScopes.length === 0
    || grantedScopes.length > 64
    || !grantedScopes.every((scope) => typeof scope === "string" && ATTIO_SCOPE_PATTERN.test(scope))
  ) {
    return false;
  }
  const grants = new Set(grantedScopes);
  return requiredScopes.every((requiredScope) => (
    grants.has(requiredScope)
    || (
      requiredScope.endsWith(":read")
      && grants.has(`${requiredScope.slice(0, -":read".length)}:read-write`)
    )
  ));
}

function exactApiOrigin(value: unknown, fallback: string, expectedOrigin: string, label: string): string {
  let url: URL;
  try {
    url = new URL(String(value ?? "").trim() || fallback);
  } catch {
    throw new Error(`${label} is invalid`);
  }
  if (
    url.origin !== expectedOrigin
    || url.username
    || url.password
    || url.port
    || (url.pathname !== "/" && url.pathname !== "")
    || url.search
    || url.hash
  ) {
    throw new Error(`${label} is untrusted`);
  }
  return url.origin;
}

function freshsalesIdentityOrigin(
  value: unknown,
  providerContext?: Readonly<Record<string, string | undefined>>,
): { origin: string; accountId: string } {
  const accountId = crmNativeAccountId(providerContext?.freshsalesBundleAlias)?.toLowerCase();
  if (!accountId) throw new Error("Freshsales bundle alias is required");
  let url: URL;
  try {
    url = new URL(
      String(value ?? "").trim()
        || `https://${accountId}.myfreshworks.com/crm/sales/`,
    );
  } catch {
    throw new Error("Freshsales API origin is invalid");
  }
  const hostname = url.hostname.toLowerCase();
  if (
    url.protocol !== "https:"
    || ![
      `${accountId}.myfreshworks.com`,
      `${accountId}.freshworks.com`,
    ].includes(hostname)
    || url.username
    || url.password
    || url.port
    || !["/crm/sales", "/crm/sales/"].includes(url.pathname)
    || url.search
    || url.hash
  ) {
    throw new Error("Freshsales API origin is outside the verified bundle alias");
  }
  return { origin: `${url.origin}/crm/sales`, accountId };
}

export function buildCrmProviderAccountIdentity(
  provider: "hubspot" | "salesforce",
  metadata: {
    hubId?: string;
    organizationId?: string;
    userId?: string;
  },
): ProviderAccountIdentity | null {
  if (provider === "hubspot") {
    const hubId = String(metadata.hubId || "").trim();
    return hubId
      ? { accountId: hubId, displayName: `HubSpot portal ${hubId}` }
      : null;
  }

  const organizationId = String(metadata.organizationId || "").trim();
  const userId = String(metadata.userId || "").trim();
  return organizationId && userId
    ? {
        accountId: `${organizationId}:${userId}`,
        displayName: `Salesforce organization ${organizationId}`,
      }
    : null;
}

export function getBaseProvider(vendorKey: string | null | undefined): string {
  return String(vendorKey || "").split(":")[0].trim();
}

export function getCanonicalProviderAccountKey(
  vendorKey: string,
  token?: Partial<SyncVendorProp> | null,
): string {
  const baseProvider = getBaseProvider(vendorKey);
  const configured = String(token?.providerAccountKey || "").trim();
  if (!CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider)) {
    if (configured.startsWith(`${baseProvider}:`) && configured.length > baseProvider.length + 1) {
      return configured;
    }
    return vendorKey;
  }
  const configuredAccountId = configured.startsWith(`${baseProvider}:`)
    ? crmNativeAccountId(configured.slice(baseProvider.length + 1))
    : undefined;
  if (configuredAccountId) {
    return `${baseProvider}:${configuredAccountId}`;
  }
  const keyedAccountId = vendorKey.startsWith(`${baseProvider}:`)
    ? crmNativeAccountId(vendorKey.slice(baseProvider.length + 1))
    : undefined;
  if (keyedAccountId) {
    return `${baseProvider}:${keyedAccountId}`;
  }
  const nativeAccountId = crmNativeAccountId(token?.providerAccountId || token?.accountId);
  return nativeAccountId && CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider)
    ? `${baseProvider}:${nativeAccountId}`
    : vendorKey;
}

/**
 * Resolve the connected account that owns a sync operation.
 *
 * Explicit account-scoped keys win. Legacy callers that only provide the base
 * provider can still be routed safely when the access token identifies the
 * account or there is exactly one connected account for that provider.
 */
export function resolveProviderSyncAccountKey(
  sessionTokens: Record<string, SyncVendorProp | undefined> | undefined | null,
  provider: string,
  requestedAccountKey?: string | null,
  accessToken?: string | null,
): string {
  const baseProvider = getBaseProvider(provider);
  const requestedKey = String(requestedAccountKey || "").trim();

  if (
    requestedKey
    && getBaseProvider(requestedKey) === baseProvider
    && sessionTokens?.[requestedKey]
    && requestedKey !== baseProvider
  ) {
    return requestedKey;
  }

  const providerAccounts = Object.entries(sessionTokens || {}).filter(
    ([key, token]) => getBaseProvider(key) === baseProvider && Boolean(token),
  ) as Array<[string, SyncVendorProp]>;

  const normalizedAccessToken = String(accessToken || "").trim();
  if (normalizedAccessToken) {
    const tokenMatches = providerAccounts.filter(
      ([, token]) => String(token.token || "").trim() === normalizedAccessToken,
    );
    if (tokenMatches.length === 1) {
      return getCanonicalProviderAccountKey(tokenMatches[0][0], tokenMatches[0][1]);
    }
  }

  if (
    requestedKey
    && getBaseProvider(requestedKey) === baseProvider
    && sessionTokens?.[requestedKey]
  ) {
    return getCanonicalProviderAccountKey(requestedKey, sessionTokens?.[requestedKey]);
  }

  if (providerAccounts.length === 1) {
    return getCanonicalProviderAccountKey(providerAccounts[0][0], providerAccounts[0][1]);
  }

  return requestedKey && getBaseProvider(requestedKey) === baseProvider
    ? requestedKey
    : baseProvider;
}

export function buildProviderAccountScopedRecordId(
  providerAccountKey: string,
  providerRecordId: string,
): string {
  const accountKey = String(providerAccountKey || "").trim();
  const recordId = String(providerRecordId || "").trim();
  if (!accountKey || !recordId) return recordId;
  return recordId.startsWith(`${accountKey}::`) ? recordId : `${accountKey}::${recordId}`;
}

export function isProviderAccountKey(vendorKey: string | null | undefined): boolean {
  return String(vendorKey || "").includes(":");
}

export type ProviderConnectionState = "disconnected" | "connected" | "attention";

/** The shared row evidence, plus the legacy client-only `connected` flag. */
function hasProviderConnectionEvidence(token: Record<string, unknown>): boolean {
  if (isOwnerDisconnectedProviderAccount(token)) return false;
  return hasProviderAccountConnectionEvidence(token) || Boolean(token.connected);
}

export function getConnectedProviderAccounts(
  sessionTokens: Record<string, SyncVendorProp | undefined> | undefined | null,
): Array<{ vendor: string; selectedApps?: readonly string[] }> {
  return Object.entries(sessionTokens || {}).flatMap(([vendor, token]) => {
    if (!token || !isUsableProviderAccountRow(token)) return [];
    return [{
      vendor,
      ...(Array.isArray(token.selectedApps)
        ? { selectedApps: token.selectedApps }
        : {}),
    }];
  });
}

/** Summarize every account row for a provider instead of trusting only its base key. */
export function getProviderConnectionState(
  sessionTokens: Record<string, Record<string, any> | undefined> | undefined | null,
  provider: string,
): ProviderConnectionState {
  const baseProvider = getBaseProvider(provider);
  const accounts = Object.entries(sessionTokens || {}).filter(
    ([vendorKey, token]) => getBaseProvider(vendorKey) === baseProvider && Boolean(token),
  ) as Array<[string, Record<string, unknown>]>;

  // An account the owner disconnected neither needs attention nor connects
  // the provider; it only keeps its imported data.
  if (accounts.some(([, token]) => providerAccountRowConnectionState(token) === "reauthorize")) {
    return "attention";
  }
  return accounts.some(([, token]) => hasProviderConnectionEvidence(token))
    ? "connected"
    : "disconnected";
}

export function getProviderAccountLabel(vendorKey: string, token?: Partial<SyncVendorProp> | null): string {
  const baseProvider = getBaseProvider(vendorKey);
  const accountEmail = typeof token?.accountEmail === "string" ? token.accountEmail : "";
  const accountName = typeof token?.accountName === "string" ? token.accountName : "";
  return accountEmail || accountName || baseProvider;
}

export function getProviderAccountIdentityDedupKey(
  vendorKey: string,
  token?: Partial<SyncVendorProp> | null
): string {
  const baseProvider = getBaseProvider(vendorKey);
  const accountId = CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider)
    ? crmNativeAccountId(token?.providerAccountId || token?.accountId)
    : stableSlug(String(token?.providerAccountId || token?.accountId || ""));
  const accountEmail = String(token?.accountEmail || "").trim().toLowerCase();

  if (accountId) return `${baseProvider}:id:${accountId}`;
  if (accountEmail) return `${baseProvider}:email:${accountEmail}`;
  return `${baseProvider}:key:${vendorKey}`;
}

function stableSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
}

function authenticatedProviderAccountIdentity(
  authenticatedUserId: unknown,
  tenantIds: unknown[] = [],
): string | undefined {
  const userId = String(authenticatedUserId ?? "").trim();
  if (!userId) return undefined;
  const tenants = Array.from(new Set(
    tenantIds.map((value) => String(value ?? "").trim()).filter(Boolean),
  )).sort();
  return tenants.length ? `${userId}::${tenants.join(",")}` : userId;
}

export function buildProviderAccountKey(
  vendor: string,
  identity?: ProviderAccountIdentity | null
): string {
  const baseProvider = getBaseProvider(vendor);
  if (CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider)) {
    const nativeAccountId = crmNativeAccountId(identity?.accountId);
    if (nativeAccountId) return `${baseProvider}:${nativeAccountId}`;
  }
  const stableId = stableSlug(identity?.accountId || identity?.email || "");
  return stableId ? `${baseProvider}:${stableId}` : baseProvider;
}

export function findProviderAccountKey(
  sessionTokens: Record<string, SyncVendorProp | undefined> | undefined | null,
  vendor: string,
  identity?: ProviderAccountIdentity | null
): string | null {
  if (!sessionTokens) return null;

  const baseProvider = getBaseProvider(vendor);
  const normalizedAccountId = CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider)
    ? crmNativeAccountId(identity?.accountId)
    : stableSlug(identity?.accountId || "");
  const normalizedEmail = String(identity?.email || "").trim().toLowerCase();

  for (const [key, token] of Object.entries(sessionTokens)) {
    if (getBaseProvider(key) !== baseProvider || !token) continue;

    const tokenAccountId = CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider)
      ? crmNativeAccountId(token.providerAccountId || token.accountId)
      : stableSlug(String(token.providerAccountId || token.accountId || ""));
    const tokenEmail = String(token.accountEmail || "").trim().toLowerCase();
    if (normalizedAccountId && tokenAccountId && normalizedAccountId === tokenAccountId) {
      return key;
    }
    // Contacts-era Salesforce identities were stored as
    // `${organizationId}:${userId}`; the canonical CRM identity is the
    // organization ID alone. Treat a stored composite whose organization
    // segment equals the incoming account ID as the same identity so
    // existing connected accounts are not silently orphaned on reconnect.
    if (baseProvider === "salesforce" && normalizedAccountId && !tokenAccountId) {
      const legacyOrganizationId = crmNativeAccountId(
        String(token.providerAccountId || token.accountId || "").split(":")[0],
      );
      if (legacyOrganizationId && legacyOrganizationId === normalizedAccountId) {
        return key;
      }
    }
    if (normalizedEmail && tokenEmail && normalizedEmail === tokenEmail) {
      return key;
    }
  }

  // An account-scoped key embeds the identity (e.g. google:<accountId>), so it
  // still identifies the entry when its stored identity fields are missing.
  const structuralKey = buildProviderAccountKey(baseProvider, identity);
  if (structuralKey !== baseProvider && sessionTokens[structuralKey]) {
    return structuralKey;
  }

  return null;
}

export function chooseProviderAccountKey(
  sessionTokens: Record<string, SyncVendorProp | undefined> | undefined | null,
  vendor: string,
  identity?: ProviderAccountIdentity | null,
  options?: { forceNewAccount?: boolean; preferScopedKey?: boolean }
): string {
  const existingKey = findProviderAccountKey(sessionTokens, vendor, identity);
  if (existingKey) return existingKey;

  const baseProvider = getBaseProvider(vendor);
  const preferredKey = buildProviderAccountKey(baseProvider, identity);
  const baseToken = sessionTokens?.[baseProvider];

  if (!baseToken) {
    return (options?.preferScopedKey || CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider))
      && preferredKey !== baseProvider
      ? preferredKey
      : baseProvider;
  }
  if (options?.forceNewAccount) {
    return preferredKey === baseProvider
      ? `${baseProvider}:account-${Date.now().toString(36)}`
      : preferredKey;
  }
  if (preferredKey === baseProvider) return baseProvider;

  const baseAccountId = CRM_NATIVE_ACCOUNT_PROVIDERS.has(baseProvider)
    ? crmNativeAccountId(baseToken.providerAccountId || baseToken.accountId)
    : stableSlug(String(baseToken.providerAccountId || baseToken.accountId || ""));
  const baseEmail = String(baseToken.accountEmail || "").trim().toLowerCase();
  if (!baseAccountId && !baseEmail && !isProviderAccountKey(vendor)) {
    return baseProvider;
  }

  return preferredKey;
}

export async function fetchProviderAccountIdentity(
  vendor: string,
  accessToken: string,
  options?: {
    providerAuthMode?: unknown;
    env?: Readonly<Record<string, string | undefined>>;
    providerApiBaseUrl?: unknown;
    providerContext?: Readonly<Record<string, string | undefined>>;
    fetchImpl?: typeof fetch;
    mfilesOAuthPlugin?: MFilesOAuthPluginConfiguration;
  },
): Promise<ProviderAccountIdentity | null> {
  const baseProvider = getBaseProvider(vendor);
  const providerFetch = options?.fetchImpl ?? fetch;
  const fetchIdentity = (input: string | URL | Request, init: RequestInit = {}) =>
    providerFetch(input, {
      ...init,
      signal: init.signal || AbortSignal.timeout(20_000),
    });

  try {
    if (baseProvider === "mfiles") {
      const plugin = options?.mfilesOAuthPlugin;
      if (!plugin) return null;
      const response = await fetchIdentity(`${plugin.serverUrl}/REST/session/userid.aspx`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-Vault": plugin.vaultId,
          "X-AuthConfig": plugin.authConfigName,
        },
      });
      if (!response.ok) return null;
      const payload = await response.json() as Record<string, unknown>;
      const userId = boundedIdentityString(payload.Value ?? payload.value, 1_024);
      return userId
        ? {
            accountId: `${plugin.vaultId}:${userId}`,
            providerTenantIds: [plugin.vaultId],
            displayName: `M-Files vault ${plugin.vaultId}`,
          }
        : null;
    }

    if (baseProvider === "hubspot") {
      const apiOrigin = exactApiOrigin(
        options?.providerApiBaseUrl,
        "https://api.hubapi.com",
        "https://api.hubapi.com",
        "HubSpot API origin",
      );
      const response = await fetchIdentity(`${apiOrigin}/account-info/2026-03/details`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const accountId = crmNativeAccountId(
        profile?.hub_id ?? profile?.portalId ?? profile?.portal_id,
      );
      return accountId
        ? {
            accountId,
            displayName: boundedIdentityString(
              profile?.companyName ?? profile?.uiDomain,
              512,
            ),
          }
        : null;
    }

    if (baseProvider === "salesforce") {
      const apiOrigin = normalizeSalesforceInstanceUrl(
        boundedIdentityString(options?.providerApiBaseUrl, 2_048) ?? "",
      );
      const response = await fetchIdentity(`${apiOrigin}/services/oauth2/userinfo`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const accountId = crmNativeAccountId(profile?.organization_id);
      return accountId
        ? {
            accountId,
            email: boundedIdentityString(profile?.email, 320),
            displayName: boundedIdentityString(profile?.name, 512),
          }
        : null;
    }

    if (baseProvider === "pipedrive") {
      const apiOrigin = normalizePipedriveApiOrigin(options?.providerApiBaseUrl);
      const response = await fetchIdentity(`${apiOrigin}/api/v1/users/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = (await response.json())?.data || {};
      const companyId = String(profile.company_id ?? "").trim();
      if (!companyId) return null;
      return {
        accountId: companyId,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.company_name === "string"
            ? profile.company_name
            : typeof profile.company_domain === "string"
              ? profile.company_domain
              : undefined,
      };
    }

    if (baseProvider === "zoho_crm") {
      const apiOrigin = normalizeZohoApiOrigin(options?.providerApiBaseUrl);
      const response = await fetchIdentity(`${apiOrigin}/crm/v8/org`, {
        headers: { Authorization: `Zoho-oauthtoken ${accessToken}` },
      });
      if (!response.ok) return null;
      const organization = (await response.json())?.org?.[0] || {};
      const organizationId = String(organization.id ?? "").trim();
      if (!organizationId) return null;
      return {
        accountId: organizationId,
        email: typeof organization.primary_email === "string" ? organization.primary_email : undefined,
        displayName: typeof organization.company_name === "string" ? organization.company_name : undefined,
      };
    }

    if (baseProvider === "dynamics_365_sales") {
      const apiOrigin = normalizeDynamics365EnvironmentUrl(options?.providerApiBaseUrl);
      const response = await fetchIdentity(`${apiOrigin}/api/data/v9.2/WhoAmI`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
          "OData-MaxVersion": "4.0",
          "OData-Version": "4.0",
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const accountId = crmNativeAccountId(profile?.OrganizationId);
      return accountId
        ? { accountId, displayName: "Dynamics 365 Sales organization" }
        : null;
    }

    if (baseProvider === "close") {
      const configured = String(options?.providerApiBaseUrl ?? "").trim()
        || "https://api.close.com/api/v1/";
      const apiBase = new URL(configured);
      if (
        apiBase.origin !== "https://api.close.com"
        || apiBase.username
        || apiBase.password
        || apiBase.port
        || !["/api/v1", "/api/v1/"].includes(apiBase.pathname)
        || apiBase.search
        || apiBase.hash
      ) {
        throw new Error("Close API base URL is untrusted");
      }
      const response = await fetchIdentity("https://api.close.com/api/v1/me/", {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const expectedAccountId = crmNativeAccountId(options?.providerContext?.accountId);
      const organizations = Array.isArray(profile?.organizations)
        ? profile.organizations
        : [];
      const organization = expectedAccountId
        ? organizations.find((candidate: Record<string, unknown>) => (
            crmNativeAccountId(candidate?.id ?? candidate?.organization_id) === expectedAccountId
          ))
        : organizations.length === 1
          ? organizations[0]
          : null;
      const accountId = crmNativeAccountId(
        profile?.organization_id
        ?? profile?.organization?.id
        ?? organization?.id
        ?? organization?.organization_id,
      );
      return accountId
        ? {
            accountId,
            email: boundedIdentityString(profile?.email, 320),
            displayName: boundedIdentityString(
              organization?.name ?? profile?.organization?.name ?? profile?.name,
              512,
            ),
          }
        : null;
    }

    if (baseProvider === "copper") {
      const configured = String(options?.providerApiBaseUrl ?? "").trim()
        || "https://api.copper.com/developer_api/v1/";
      const apiBase = new URL(configured);
      if (
        apiBase.origin !== "https://api.copper.com"
        || apiBase.username
        || apiBase.password
        || apiBase.port
        || !["/developer_api/v1", "/developer_api/v1/"].includes(apiBase.pathname)
        || apiBase.search
        || apiBase.hash
      ) {
        throw new Error("Copper API base URL is untrusted");
      }
      const userEmail = boundedIdentityString(options?.providerContext?.userEmail, 320);
      if (!userEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) return null;
      if (options?.providerContext?.application !== "developer_api") return null;
      const response = await fetchIdentity("https://api.copper.com/developer_api/v1/account", {
        headers: {
          Accept: "application/json",
          "X-PW-AccessToken": accessToken,
          "X-PW-Application": "developer_api",
          "X-PW-UserEmail": userEmail,
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const accountId = crmNativeAccountId(profile?.id ?? profile?.account_id);
      return accountId
        ? {
            accountId,
            email: userEmail,
            displayName: boundedIdentityString(profile?.name ?? profile?.company_name, 512),
          }
        : null;
    }

    if (baseProvider === "freshsales") {
      const { origin, accountId } = freshsalesIdentityOrigin(
        options?.providerApiBaseUrl,
        options?.providerContext,
      );
      const response = await fetchIdentity(`${origin}/api/selector/owners`, {
        headers: {
          Accept: "application/json",
          Authorization: `Token token=${accessToken}`,
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const owners = Array.isArray(profile?.owners)
        ? profile.owners
        : Array.isArray(profile)
          ? profile
          : [];
      if (owners.length === 0) return null;
      return {
        accountId,
        displayName: `${accountId} Freshsales`,
      };
    }

    if (baseProvider === "attio") {
      const apiOrigin = exactApiOrigin(
        options?.providerApiBaseUrl,
        "https://api.attio.com",
        "https://api.attio.com",
        "Attio API origin",
      );
      const response = await fetchIdentity(`${apiOrigin}/v2/self`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) return null;
      const profile = await response.json() as unknown;
      if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
        return null;
      }
      const profileRecord = profile as Record<string, unknown>;
      if (profileRecord.active !== true) return null;
      const accountId = crmNativeAccountId(profileRecord.workspace_id);
      const grantedScopes = parseAttioGrantedScopes(profileRecord.scope);
      const requiredScopes = ["record_permission:read", "object_configuration:read"] as const;
      const displayName = boundedIdentityString(profileRecord.workspace_name, 512);
      return accountId && grantedScopes && attioScopesMeetReadRequirements(grantedScopes, requiredScopes)
        ? {
            accountId,
            ...(displayName ? { displayName } : {}),
            grantedScopes,
          }
        : null;
    }

    if (
      baseProvider === "google"
      || baseProvider === "googlechat"
      || baseProvider === "youtube"
      || baseProvider === "google_business_profile"
      || baseProvider === "google_meet"
    ) {
      const response = await fetchIdentity("https://openidconnect.googleapis.com/v1/userinfo", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const accountId = typeof profile.sub === "string" && profile.sub.trim()
        ? profile.sub.trim()
        : undefined;
      if (baseProvider === "googlechat" && !accountId) return null;
      return {
        accountId,
        // Google vouches for the address only when it reports it verified.
        email: typeof profile.email === "string" && profile.email_verified === true ? profile.email : undefined,
        displayName: typeof profile.name === "string" ? profile.name : undefined,
      };
    }

    if (baseProvider === "x") {
      const response = await fetchIdentity(
        "https://api.x.com/2/users/me?user.fields=id,name,username",
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) return null;
      const profile = (await response.json())?.data || {};
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        displayName:
          typeof profile.name === "string"
            ? profile.name
            : typeof profile.username === "string"
              ? profile.username
              : undefined,
      };
    }

    if (baseProvider === "meta") {
      const response = await fetchIdentity(
        "https://graph.facebook.com/v23.0/me?fields=id,name",
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        displayName: typeof profile.name === "string" ? profile.name : undefined,
      };
    }

    if (baseProvider === "tiktok") {
      const response = await fetchIdentity(
        "https://open.tiktokapis.com/v2/user/info/?fields=open_id,display_name,username",
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) return null;
      const profile = (await response.json())?.data?.user || {};
      return {
        accountId: typeof profile.open_id === "string" ? profile.open_id : undefined,
        displayName:
          typeof profile.display_name === "string"
            ? profile.display_name
            : typeof profile.username === "string"
              ? profile.username
              : undefined,
      };
    }

    if (
      baseProvider === "microsoft"
      || baseProvider === "teams"
      || baseProvider === "microsoft_teams_meetings"
    ) {
      const response = await fetchIdentity(
        "https://graph.microsoft.com/v1.0/me?$select=id,displayName,userPrincipalName,mail",
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        email: microsoftVerifiedEmail(profile.userPrincipalName, profile.mail),
        displayName: typeof profile.displayName === "string" ? profile.displayName : undefined,
      };
    }

    if (baseProvider === "fastmail") {
      const response = await fetchIdentity("https://api.fastmail.com/jmap/session", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const session = await response.json();
      const accountId = boundedIdentityString(
        session?.primaryAccounts?.["urn:ietf:params:jmap:mail"],
        1_024,
      );
      const account = accountId ? session?.accounts?.[accountId] : null;
      return accountId
        ? {
            accountId,
            email: boundedIdentityString(session?.username, 320),
            displayName: boundedIdentityString(account?.name, 512),
          }
        : null;
    }

    if (baseProvider === "zoho") {
      const config = getZohoOAuthPublicConfig(options?.env ?? process.env);
      if (!config) return null;
      const response = await fetchIdentity(`${config.mailApiBaseUrl}/accounts`, {
        headers: {
          Accept: "application/json",
          Authorization: `Zoho-oauthtoken ${accessToken}`,
        },
      });
      if (!response.ok) return null;
      const payload = await response.json();
      const accounts = Array.isArray(payload?.data) ? payload.data : [];
      const account = accounts.find(
        (candidate: Record<string, unknown>) =>
          candidate?.type === "ZOHO_ACCOUNT" &&
          (typeof candidate?.accountId === "string" ||
            typeof candidate?.accountId === "number"),
      );
      if (!account) return null;
      const accountId = String(account.accountId).trim();
      if (!accountId || accountId.length > 1_024) return null;
      return {
        accountId,
        email: boundedIdentityString(account.primaryEmailAddress, 320),
        displayName:
          boundedIdentityString(account.displayName, 512) ??
          boundedIdentityString(account.accountDisplayName, 512),
      };
    }

    if (baseProvider === "zoom") {
      const response = await fetchIdentity("https://api.zoom.us/v2/users/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.display_name === "string"
            ? profile.display_name
            : [profile.first_name, profile.last_name].filter((value) => typeof value === "string").join(" ") || undefined,
      };
    }

    if (baseProvider === "webex") {
      const response = await fetchIdentity("https://webexapis.com/v1/people/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        email: Array.isArray(profile.emails) && typeof profile.emails[0] === "string"
          ? profile.emails[0]
          : undefined,
        displayName: typeof profile.displayName === "string" ? profile.displayName : undefined,
      };
    }

    if (baseProvider === "goto_meeting") {
      const response = await fetchIdentity("https://api.getgo.com/identity/v1/Users/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId:
          typeof profile.organizerKey === "string"
            ? profile.organizerKey
            : typeof profile.id === "string"
              ? profile.id
              : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.name === "string"
            ? profile.name
            : [profile.firstName, profile.lastName].filter((value) => typeof value === "string").join(" ") || undefined,
      };
    }

    if (baseProvider === "slack") {
      const response = await fetchIdentity("https://slack.com/api/auth.test", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      if (profile?.ok === false) return null;
      const userLabel = typeof profile.user === "string" ? profile.user : "";
      const teamLabel = typeof profile.team === "string" ? profile.team : "";
      return {
        accountId: [profile.team_id, profile.user_id].filter(Boolean).join(":") || undefined,
        email: userLabel.includes("@") ? userLabel : undefined,
        displayName: [userLabel, teamLabel ? `(${teamLabel})` : ""].filter(Boolean).join(" ") || undefined,
      };
    }

    if (baseProvider === "linkedin") {
      const communityMode = options?.providerAuthMode === "community";
      const response = await fetchIdentity(
        communityMode
          ? "https://api.linkedin.com/v2/me"
          : "https://api.linkedin.com/v2/userinfo",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) return null;
      const profile = await response.json();
      if (communityMode) {
        const firstName =
          typeof profile.localizedFirstName === "string"
            ? profile.localizedFirstName
            : "";
        const lastName =
          typeof profile.localizedLastName === "string"
            ? profile.localizedLastName
            : "";
        return {
          accountId: typeof profile.id === "string" ? profile.id : undefined,
          displayName: [firstName, lastName].filter(Boolean).join(" ") || undefined,
        };
      }
      return {
        accountId: typeof profile.sub === "string" ? profile.sub : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName: typeof profile.name === "string" ? profile.name : undefined,
      };
    }

    if (baseProvider === "discord") {
      const response = await fetchIdentity("https://discord.com/api/users/@me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.global_name === "string"
            ? profile.global_name
            : typeof profile.username === "string"
              ? profile.username
              : undefined,
      };
    }

    if (baseProvider === "spotify") {
      const response = await fetchIdentity("https://api.spotify.com/v1/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName: typeof profile.display_name === "string" ? profile.display_name : undefined,
      };
    }

    if (baseProvider === "reddit") {
      const response = await fetchIdentity("https://oauth.reddit.com/api/v1/me", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "User-Agent": "SmartManager/1.0",
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        displayName: typeof profile.name === "string" ? profile.name : undefined,
      };
    }

    if (baseProvider === "whatsapp") {
      const graphVersion = (
        process.env.WHATSAPP_GRAPH_API_VERSION
        || process.env.NEXT_PUBLIC_WHATSAPP_GRAPH_API_VERSION
        || ""
      ).trim();
      if (!/^v\d+\.\d+$/.test(graphVersion)) return null;
      const response = await fetchIdentity(`https://graph.facebook.com/${graphVersion}/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        displayName: typeof profile.name === "string" ? profile.name : undefined,
      };
    }

    if (baseProvider === "calendly") {
      const response = await fetchIdentity("https://api.calendly.com/users/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const payload = await response.json();
      const resource = payload?.resource || {};
      return {
        accountId: typeof resource.uri === "string" ? resource.uri : undefined,
        email: typeof resource.email === "string" ? resource.email : undefined,
        displayName: typeof resource.name === "string" ? resource.name : undefined,
      };
    }

    if (baseProvider === "asana") {
      const response = await fetchIdentity("https://app.asana.com/api/1.0/users/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = (await response.json())?.data || {};
      return {
        accountId: typeof profile.gid === "string" ? profile.gid : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName: typeof profile.name === "string" ? profile.name : undefined,
      };
    }

    if (baseProvider === "trello") {
      const apiKey = process.env.NEXT_PUBLIC_TRELLO_API_KEY?.trim();
      if (!apiKey) return null;
      const url = new URL("https://api.trello.com/1/members/me");
      url.searchParams.set("fields", "id,fullName,username,email");
      const oauthEncode = (value: string) => encodeURIComponent(value).replace(
        /[!'()*]/g,
        (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
      );
      const response = await fetchIdentity(url, {
        headers: {
          Authorization: [
            "OAuth",
            `oauth_consumer_key="${oauthEncode(apiKey)}",`,
            `oauth_token="${oauthEncode(accessToken)}"`,
          ].join(" "),
        },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: typeof profile.id === "string" ? profile.id : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.fullName === "string"
            ? profile.fullName
            : typeof profile.username === "string"
              ? profile.username
              : undefined,
      };
    }

    if (baseProvider === "jira") {
      const response = await fetchIdentity(
        "https://api.atlassian.com/oauth/token/accessible-resources",
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) return null;
      const resources = await response.json();
      if (!Array.isArray(resources) || resources.length === 0) return null;
      const ids = resources
        .map((resource) => typeof resource?.id === "string" ? resource.id : "")
        .filter(Boolean)
        .sort();
      const names = resources
        .map((resource) => typeof resource?.name === "string" ? resource.name : "")
        .filter(Boolean);
      const cloudId = ids[0];
      if (!cloudId) return null;
      const userResponse = await fetchIdentity(
        `https://api.atlassian.com/ex/jira/${encodeURIComponent(cloudId)}/rest/api/3/myself`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!userResponse.ok) return null;
      const user = await userResponse.json();
      const userIdentity = typeof user.accountId === "string"
        ? user.accountId
        : typeof user.emailAddress === "string"
          ? user.emailAddress.trim().toLowerCase()
          : "";
      return {
        accountId: authenticatedProviderAccountIdentity(userIdentity, ids),
        providerTenantIds: ids,
        email: typeof user.emailAddress === "string" ? user.emailAddress : undefined,
        displayName:
          typeof user.displayName === "string"
            ? user.displayName
            : names.join(", ") || "Atlassian Jira Cloud",
      };
    }

    if (baseProvider === "monday") {
      const response = await fetchIdentity("https://api.monday.com/v2", {
        method: "POST",
        headers: {
          Authorization: accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: "query { me { id name email account { id name } } }" }),
      });
      if (!response.ok) return null;
      const profile = (await response.json())?.data?.me || {};
      const userIdentity = profile.id != null
        ? String(profile.id)
        : typeof profile.email === "string"
          ? profile.email.trim().toLowerCase()
          : "";
      return {
        accountId: authenticatedProviderAccountIdentity(
          userIdentity,
          profile.account?.id != null ? [profile.account.id] : [],
        ),
        providerTenantIds: profile.account?.id != null
          ? [String(profile.account.id)]
          : [],
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.account?.name === "string"
            ? profile.account.name
            : typeof profile.name === "string"
              ? profile.name
              : undefined,
      };
    }

    if (baseProvider === "clickup") {
      const response = await fetchIdentity("https://api.clickup.com/api/v2/user", {
        headers: { Authorization: accessToken },
      });
      if (!response.ok) return null;
      const profile = (await response.json())?.user || {};
      return {
        accountId: profile.id != null ? String(profile.id) : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.username === "string" ? profile.username : undefined,
      };
    }

    if (baseProvider === "linear") {
      const response = await fetchIdentity("https://api.linear.app/graphql", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: "query SmartManagerIdentity { viewer { id name email organization { id name } } }",
        }),
      });
      if (!response.ok) return null;
      const profile = (await response.json())?.data?.viewer || {};
      const userIdentity = typeof profile.id === "string"
        ? profile.id
        : typeof profile.email === "string"
          ? profile.email.trim().toLowerCase()
          : "";
      return {
        accountId: authenticatedProviderAccountIdentity(
          userIdentity,
          typeof profile.organization?.id === "string"
            ? [profile.organization.id]
            : [],
        ),
        providerTenantIds: typeof profile.organization?.id === "string"
          ? [profile.organization.id]
          : [],
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.organization?.name === "string"
            ? profile.organization.name
            : typeof profile.name === "string"
              ? profile.name
              : undefined,
      };
    }

    if (baseProvider === "basecamp") {
      const userAgent = process.env.BASECAMP_API_USER_AGENT?.trim();
      if (!userAgent) return null;
      const response = await fetchIdentity(
        "https://launchpad.37signals.com/authorization.json",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "User-Agent": userAgent,
          },
        },
      );
      if (!response.ok) return null;
      const profile = await response.json();
      const accounts = Array.isArray(profile?.accounts)
        ? profile.accounts.filter((account: any) => account?.product === "bc3")
        : [];
      const accountIds = accounts
        .map((account: any) => account?.id != null ? String(account.id) : "")
        .filter(Boolean);
      if (accountIds.length === 0) return null;
      const identityEmail = typeof profile?.identity?.email_address === "string"
        ? profile.identity.email_address
        : undefined;
      const userIdentity = profile?.identity?.id != null
        ? String(profile.identity.id)
        : identityEmail?.trim().toLowerCase();
      return {
        accountId: authenticatedProviderAccountIdentity(userIdentity, accountIds),
        providerTenantIds: accountIds,
        email: identityEmail,
        displayName: typeof profile?.identity?.first_name === "string"
          ? [profile.identity.first_name, profile.identity.last_name]
              .filter((value) => typeof value === "string" && value)
              .join(" ")
          : undefined,
      };
    }

    if (baseProvider === "todoist") {
      const response = await fetchIdentity("https://api.todoist.com/api/v1/user", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      return {
        accountId: profile.id != null ? String(profile.id) : undefined,
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName: typeof profile.full_name === "string" ? profile.full_name : undefined,
      };
    }
    if (baseProvider === "calcom") {
      const response = await fetchIdentity("https://api.cal.com/v2/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = (await response.json())?.data || {};
      return {
        accountId: profile.id == null ? undefined : String(profile.id),
        email: typeof profile.email === "string" ? profile.email : undefined,
        displayName:
          typeof profile.name === "string"
            ? profile.name
            : typeof profile.username === "string"
              ? profile.username
              : undefined,
      };
    }

    if (baseProvider === "acuity") {
      const response = await fetchIdentity("https://acuityscheduling.com/api/v1/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return null;
      const profile = await response.json();
      const accountId = profile.id ?? profile.userID ?? profile.userId;
      return {
        accountId: accountId == null ? undefined : String(accountId),
        email: typeof profile.email === "string" ? profile.email : undefined,
      };
    }
  } catch (error) {
    console.warn(`Unable to resolve ${baseProvider} account identity:`, logSafeError(error));
  }

  return null;
}
